import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";
import { Formik, Form } from "formik";
import * as Yup from "yup";

const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  // Get user data from location state for editing
  const userData = location.state?.user || null;
  const isEdit = !!userData;

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be less than 50 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be less than 50 characters"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required")
      .max(100, "Email must be less than 100 characters"),
    password: isEdit
      ? Yup.string()
          .min(6, "Password must be at least 6 characters")
          .max(100, "Password must be less than 100 characters")
      : Yup.string()
          .required("Password is required")
          .min(6, "Password must be at least 6 characters")
          .max(100, "Password must be less than 100 characters"),
    role: Yup.string().required("Role is required"),
    phone: Yup.string()
      .trim()
      .when("role", {
        is: (role) => role !== "admin",
        then: (schema) =>
          schema
            .matches(/^\d{10}$/, "Phone number must be 10 digits")
            .required("Phone number is required for non-admin users"),
        otherwise: (schema) =>
          schema.matches(/^(\d{10})?$/, "Phone number must be 10 digits"),
      }),
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get("/roles");
      setRoles(
        response.data.roles || [
          { _id: "user", name: "User" },
          { _id: "admin", name: "Admin" },
        ]
      );
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      // Fallback to default roles if API fails
      setRoles([
        { _id: "user", name: "User" },
        { _id: "admin", name: "Admin" },
      ]);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      // Remove password from values if it's empty in edit mode
      const submitData = { ...values };
      if (isEdit && !submitData.password) {
        delete submitData.password;
      }

      if (submitData.phone) {
        const digits = submitData.phone.replace(/\D/g, "").slice(-10);
        submitData.phone = digits.length === 10 ? digits : "";
      }

      if (!submitData.phone) {
        if (submitData.role === "admin") {
          delete submitData.phone;
        }
      }

      if (isEdit) {
        await api.put(`/users/${userData._id}`, submitData);
        toast.success("User updated successfully");
      } else {
        await api.post("/users", submitData);
        toast.success("User created successfully");
      }

      navigate("/users");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} user`
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/users");
  };

  const initialValues = {
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || "",
    email: userData?.email || "",
    password: "",
    role: userData?.role?._id || userData?.role || "",
    phone: userData?.phone || "",
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Back to Users
        </Button>
        <Typography variant="h4">
          {isEdit ? "Edit User" : "Create New User"}
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {isEdit && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Leave password field empty to keep the current password
                      unchanged.
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    disabled={loading}
                    placeholder={
                      isEdit ? "Leave empty to keep current password" : ""
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={touched.role && Boolean(errors.role)}
                  >
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={values.role}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.value === "admin") {
                          setFieldValue("phone", "");
                        }
                      }}
                      onBlur={handleBlur}
                      disabled={loading}
                    >
                      {roles.map((role) => (
                        <MenuItem
                          key={role._id || role.name}
                          value={role._id || role.name}
                        >
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.role && errors.role && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, ml: 2 }}
                      >
                        {errors.role}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {values.role && values.role !== "admin" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number (+91)"
                      name="phone"
                      value={values.phone}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFieldValue("phone", digits);
                      }}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      disabled={loading}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <SaveIcon />
                      }
                      disabled={loading || isSubmitting}
                    >
                      {loading
                        ? "Saving..."
                        : isEdit
                        ? "Update User"
                        : "Create User"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default UserForm;
